/**
 * The shape every section after the hero shares.
 *
 * Eyebrow, title, one-sentence claim, figure, proof line — in that order, on
 * every band. The repetition is the argument: a reader who works out the rhythm
 * once can skim ten sections and know where the claim is and where the evidence
 * for it is, without reading the prose in between.
 *
 * The proof line is not optional, and that is enforced by the type. A section
 * that cannot name the command proving its claim is a section making a claim
 * this page has no business making.
 */
import type { ReactNode } from 'react'

import { Reveal } from '../kit'
import { useT } from '../i18n/context'
import type { MessageKey } from '../i18n'

export function Section({
  id,
  index,
  eyebrow,
  title,
  sub,
  proof,
  children,
  figure,
  tone,
}: {
  id: string
  /**
   * Which band this is on its page, counting from one.
   *
   * Seven bands of identical shape is a page a reader cannot place themselves
   * in — the question it provokes is not "what does this say" but "how many
   * more of these are there". The number answers it, and it is the same number
   * the page's index printed beside the same word, so the two read as one
   * structure rather than as a list and then some sections.
   *
   * Optional because a page may carry a single band, where "01" of nothing is
   * a promise of more that is not coming.
   */
  index?: number
  eyebrow: MessageKey
  title: MessageKey
  sub: MessageKey
  /** The command that proves the section's claim. Required, deliberately. */
  proof: MessageKey
  children?: ReactNode
  figure: ReactNode
  /** `sunken` alternates the band background so ten sections do not read as one. */
  tone?: 'sunken'
}) {
  const t = useT()
  const headingId = `${id}-title`

  return (
    <section
      id={id}
      className={`kl-section ${tone === 'sunken' ? 'kl-section--sunken' : ''}`}
      aria-labelledby={headingId}
    >
      <div className="kl-inner">
        <Reveal className="kl-head">
          <p className="k-eyebrow">
            {index === undefined ? null : (
              // `aria-hidden`: the heading beneath already names the band, and
              // a screen reader announcing "zero three" before it is a machine
              // fact read out as though it were part of the sentence.
              <span className="kl-section__index k-mono" aria-hidden="true">
                {String(index).padStart(2, '0')}
              </span>
            )}
            <span className="k-label">{t(eyebrow)}</span>
          </p>
          <h2 id={headingId} className="kl-title">
            {t(title)}
          </h2>
          <p className="kl-sub">{t(sub)}</p>
        </Reveal>

        <div className="kl-section__figure">{figure}</div>

        {children ? <div className="kl-section__body">{children}</div> : null}

        <p className="kl-proof">
          <span className="kl-proof__label">{t('ui.proof')}</span>
          <span className="kl-proof__cmd k-mono">{t(proof)}</span>
        </p>
      </div>
    </section>
  )
}

/** A paragraph of section prose. Here so the class name is written once. */
export function Body({ children }: { children: ReactNode }) {
  return <p className="kl-body">{children}</p>
}

/** The one raised voice a section is allowed. */
export function Pull({ children }: { children: ReactNode }) {
  return <p className="kl-pull">{children}</p>
}

/**
 * A figure's accessible description.
 *
 * Every figure on this page is decorative markup wrapping a real argument, and
 * the argument has to survive somebody who cannot see it. `role="img"` with a
 * label would flatten the figure to one string and throw away the machine values
 * inside it; instead the figure keeps its real content and this adds the summary
 * a sighted reader gets from the layout itself.
 */
export function FigureFrame({
  alt,
  children,
  className,
}: {
  alt: string
  children: ReactNode
  className?: string
}) {
  return (
    <figure className={`kl-figure ${className ?? ''}`}>
      {children}
      <figcaption className="k-visually-hidden">{alt}</figcaption>
    </figure>
  )
}
